import React from 'react';
import { useTheme } from '../../../../contexts/ThemeContext';
import { cn } from '../../../../lib/utils';
import { formatDate } from '../../../../config/appConfig';
import type { ClientSummary, ClientSummaryCounts } from '../../useData';

interface ClientSummaryKpisProps {
  clientSummary: ClientSummary;
  onScrollToModule?: (key: string) => void;
}

interface KpiCard {
  key: string;
  label: string;
  value: string;
  scrollKey?: string;
  icon: React.ReactNode;
  accent?: string;
}

const ClientSummaryKpis: React.FC<ClientSummaryKpisProps> = ({ clientSummary, onScrollToModule }) => {
  const { colors } = useTheme();
  const counts: ClientSummaryCounts = clientSummary._count ?? {};

  const cards: KpiCard[] = [];

  if (clientSummary.kpis?.nextAppointmentDate) {
    cards.push({
      key: 'next-appointment',
      label: 'Next appointment',
      value: formatDate(clientSummary.kpis.nextAppointmentDate),
      scrollKey: 'appointments',
      accent: 'text-blue-600 dark:text-blue-400',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
    });
  }

  const countCards: Array<{ key: keyof ClientSummaryCounts; label: string; scrollKey: string; accent: string; icon: React.ReactNode }> = [
    {
      key: 'clientAppointments',
      label: 'Appointments',
      scrollKey: 'appointments',
      accent: 'text-indigo-600 dark:text-indigo-400',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      key: 'clientTodos',
      label: 'Todos',
      scrollKey: 'todos',
      accent: 'text-emerald-600 dark:text-emerald-400',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      ),
    },
    {
      key: 'crm_ClientsContacts',
      label: 'People',
      scrollKey: 'contacts',
      accent: 'text-violet-600 dark:text-violet-400',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
    },
    {
      key: 'linkedEmails',
      label: 'Linked emails',
      scrollKey: 'linked-emails',
      accent: 'text-amber-600 dark:text-amber-400',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      key: 'FileAttachment',
      label: 'Attachments',
      scrollKey: 'attachments',
      accent: 'text-slate-600 dark:text-slate-400',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
        </svg>
      ),
    },
  ];

  for (const c of countCards) {
    const n = counts[c.key] ?? 0;
    if (n > 0 || c.key === 'clientTodos' || c.key === 'clientAppointments') {
      cards.push({
        key: c.key,
        label: c.label,
        value: String(n),
        scrollKey: c.scrollKey,
        accent: c.accent,
        icon: c.icon,
      });
    }
  }

  if (cards.length === 0) return null;

  return (
    <div className="mb-6">
      <h3 className={cn('text-xs font-semibold uppercase tracking-wider mb-3', colors.textSecondary)}>
        Key metrics
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
        {cards.map((card) => {
          const inner = (
            <>
              <div className="flex items-center gap-2">
                <span className={cn('rounded-lg p-1.5', colors.surfaceHover, card.accent)} aria-hidden>
                  {card.icon}
                </span>
                <p className={cn('text-xs font-medium truncate', colors.textSecondary)}>{card.label}</p>
              </div>
              <p className={cn('text-sm font-semibold mt-0.5 truncate', colors.text)}>{card.value}</p>
            </>
          );

          if (card.scrollKey && onScrollToModule && (counts[card.key as keyof ClientSummaryCounts] ?? 0) > 0) {
            return (
              <button
                key={card.key}
                type="button"
                onClick={() => onScrollToModule(card.scrollKey!)}
                className={cn(
                  'rounded-xl border p-3 min-w-0 text-left transition-colors hover:opacity-90',
                  colors.border,
                  colors.background,
                )}
              >
                {inner}
              </button>
            );
          }

          return (
            <div key={card.key} className={cn('rounded-xl border p-3 min-w-0', colors.border, colors.background)}>
              {inner}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ClientSummaryKpis;
